import {
    Currency,
    DemeterChartSeriesSourceModel,
    DemeterChartSeriesStyle,
    DemeterChartSourceDataModel,
    DemeterDataFrequency,
    DemeterFilterTimeSpan,
    DemeterPageElementModel,
    DemeterPageType,
    LeadingIndicatorType,
    MarketIndicatorDataCombinationType,
    MarketIndicatorTransformationType,
    MarketPricesTimeSpan,
    UnitOfMeasure,
    UpsertDemeterChartRequest,
} from '../../../Generated/Raven-Demeter';
import { chartColors, ChartDisplayType } from '../../Components/Charts/ChartDefinitions';
import { IPageElementModalSelection } from '../../Components/Modals/PageElementModal/PageElementModalDefinitions';
import { TableHeader } from '../../Components/Tables/Table/TableDefinitions';
import languageService from '../../Services/Language/LanguageService';
import { translationKeys } from '../../Services/Language/TranslationDefinitions';

export const defaultChartStyle = DemeterChartSeriesStyle.Line;
const defaultIncludeInChart = true;
const defaultShowTimespanControls = false;
const defaultShowTimespanOptions = true;
const defaultTimeSpanOptions: MarketPricesTimeSpan[] = [
    MarketPricesTimeSpan.SixMonths,
    MarketPricesTimeSpan.OneYear,
    MarketPricesTimeSpan.TwoYears,
    MarketPricesTimeSpan.FiveYears,
    MarketPricesTimeSpan.All,
];
const defaultFilterTimeSpan = MarketPricesTimeSpan.SixMonths;
const defaultDataFrequency = DemeterDataFrequency.Monthly;
const defaultAxisForFirstSeries = 0;
const defaultAxisForSecondSeries = 1;

export const defaultLabels = ['', ''];

export const defaultChartName = 'Custom Chart';

export const defaultDataSource = {
    style: defaultChartStyle,
    axis: defaultAxisForFirstSeries,
    includeInChart: defaultIncludeInChart,
    dataSourceTags: [] as string[],
    filterTimeSpan: defaultFilterTimeSpan,
} as DataSourceSelection;

export const defaultChartBuilderOptions = {
    name: defaultChartName,
    showTimespanControls: defaultShowTimespanControls,
    showTimespanOptions: defaultShowTimespanOptions,
    timespanOptions: defaultTimeSpanOptions,
    labels: defaultLabels,
    rows: [
        {
            style: defaultChartStyle,
            sources: [{ ...defaultDataSource, color: chartColors.lineChartColorsRuleSetOne[0] }],
            rows: [],
        },
        {
            style: defaultChartStyle,
            sources: [{ ...defaultDataSource, axis: defaultAxisForSecondSeries, color: chartColors.lineChartColorsRuleSetOne[1] }],
            rows: [],
        },
    ],
};

export type DataSourceSelection = DemeterChartSeriesSourceModel &
    DemeterChartSourceDataModel &
    DemeterPageElementModel &
    IPageElementModalSelection & { includeForecastData: boolean; dataSourceTags: string[] };

export type ChartBuilderDataSeries = {
    currency?: Currency;
    unitOfMeasure?: UnitOfMeasure;
    transformationType?: MarketIndicatorTransformationType;
    chartType?: ChartDisplayType;
    dataFrequency?: DemeterDataFrequency;
    dataCombinationType?: MarketIndicatorDataCombinationType;
    timeSpan?: DemeterFilterTimeSpan;
    dataSources?: DataSourceSelection[];
};

export const leadingIndicatorTypeMap = [
    {
        pageType: DemeterPageType.Domestics,
        leadingIndicatorType: LeadingIndicatorType.CommodityMonthlyDomestics,
    },
    {
        pageType: DemeterPageType.Exports,
        leadingIndicatorType: LeadingIndicatorType.CommodityMonthlyExports,
    },
    {
        pageType: DemeterPageType.Futures,
        leadingIndicatorType: LeadingIndicatorType.CommodityFuturesPrice,
    },
    {
        pageType: DemeterPageType.Imports,
        leadingIndicatorType: LeadingIndicatorType.CommodityMonthlyImports,
    },
    {
        pageType: DemeterPageType.PhysicalPrices,
        leadingIndicatorType: LeadingIndicatorType.CommodityMonthlyPrices,
    },
    {
        pageType: DemeterPageType.Production,
        leadingIndicatorType: LeadingIndicatorType.CommodityMonthlyProduction,
    },
    {
        pageType: DemeterPageType.Stocks,
        leadingIndicatorType: LeadingIndicatorType.CommodityMonthlyStocks,
    },
    {
        pageType: DemeterPageType.UserData,
        leadingIndicatorType: LeadingIndicatorType.UserData,
    },
];

export type ChartBuilderRequest = Omit<UpsertDemeterChartRequest, 'demeterChartGuid'> & { sources?: DataSourceSelection[]; demeterChartGuid?: string };

export type ListChartTableRow = {
    chartName: JSX.Element;
    modifiedBy: string;
    modifiedAt: string;
    actions: JSX.Element;
};

export const listChartsTableHeaders: TableHeader<ListChartTableRow>[] = [
    {
        headerValueGetter: () => languageService.translate(translationKeys.chartBuilder.headers.chartName),
        field: 'chartName',
        elementField: 'chartName',
        dataFormat: 'element',
        minimumWidth: 150,
    },
    {
        headerValueGetter: () => languageService.translate(translationKeys.text.savedBy),
        field: 'modifiedBy',
        dataFormat: 'string',
        minimumWidth: 150,
    },

    {
        headerValueGetter: () => languageService.translate(translationKeys.text.lastUpdated),
        field: 'modifiedAt',
        dataFormat: 'date',
        minimumWidth: 150,
    },
    {
        headerValueGetter: () => languageService.translate(translationKeys.words.actions),
        field: 'actions',
        elementField: 'actions',
        dataFormat: 'element',
        minimumWidth: 150,
    },
];
